import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { Calendar, Users, Clock, Plus, ChevronLeft, ChevronRight, Package, Building, Sun, Moon } from 'lucide-react';

function RawMaterialDutyRoster() {
  const { API } = useContext(AppContext);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState('Company');

  useEffect(() => {
    fetchRoster();
  }, [selectedDate]);

  const fetchRoster = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/raw-material/roster`, {
        params: { date: selectedDate },
        
      });
      setRoster(response.data);
    } catch (error) {
      // Fallback mock data
      setRoster([
        // Day Shift - Company Employees
        { id: '1', employee: 'Suresh Kumar', employeeType: 'Company', role: 'Material Supervisor', shift: 'Day', time: '06:00 - 18:00', section: 'Receiving', status: 'On Duty' },
        { id: '2', employee: 'Ramesh Yadav', employeeType: 'Company', role: 'Forklift Operator', shift: 'Day', time: '06:00 - 18:00', section: 'Storage', status: 'On Duty' },
        { id: '3', employee: 'Prakash Singh', employeeType: 'Company', role: 'Quality Checker', shift: 'Day', time: '06:00 - 18:00', section: 'Inspection', status: 'On Duty' },
        // Night Shift - Company Employees
        { id: '4', employee: 'Dinesh Sharma', employeeType: 'Company', role: 'Material Supervisor', shift: 'Night', time: '18:00 - 06:00', section: 'All Areas', status: 'Scheduled' },
        { id: '5', employee: 'Mohan Das', employeeType: 'Company', role: 'Weighbridge Operator', shift: 'Night', time: '18:00 - 06:00', section: 'Weighbridge', status: 'Scheduled' },
        // Day Shift - Contractor Employees
        { id: '6', employee: 'Raju Prasad', employeeType: 'Contractor', role: 'Material Handler', shift: 'Day', time: '06:00 - 18:00', section: 'Receiving', status: 'On Duty' },
        { id: '7', employee: 'Gopal Verma', employeeType: 'Contractor', role: 'Loader', shift: 'Day', time: '06:00 - 18:00', section: 'Storage', status: 'On Duty' },
        { id: '8', employee: 'Vijay Kumar', employeeType: 'Contractor', role: 'Helper', shift: 'Day', time: '06:00 - 18:00', section: 'Dispatch', status: 'On Duty' },
        // Night Shift - Contractor Employees
        { id: '9', employee: 'Shankar Das', employeeType: 'Contractor', role: 'Security', shift: 'Night', time: '18:00 - 06:00', section: 'Gate', status: 'Scheduled' },
        { id: '10', employee: 'Rajendra Mishra', employeeType: 'Contractor', role: 'Helper', shift: 'Night', time: '18:00 - 06:00', section: 'Storage', status: 'Scheduled' },
      ]);
    }
    setLoading(false);
  };

  const navigateDate = (direction) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + direction);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const getShiftColor = (shift) => {
    switch (shift) {
      case 'Day': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Night': return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'On Duty': return 'bg-green-100 text-green-700';
      case 'Scheduled': return 'bg-blue-100 text-blue-700';
      case 'Off': return 'bg-gray-100 text-gray-500';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const filteredRoster = roster.filter(r => r.employeeType === activeTab);

  const stats = {
    totalCompany: roster.filter(r => r.employeeType === 'Company').length,
    totalContractor: roster.filter(r => r.employeeType === 'Contractor').length,
    dayShift: roster.filter(r => r.shift === 'Day').length,
    nightShift: roster.filter(r => r.shift === 'Night').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a237e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="raw-material-duty-roster-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Raw Material Duty Roster</h1>
          <p className="text-gray-500 mt-1">Manage raw material team shift schedules (Day & Night)</p>
        </div>
        <button className="bg-gradient-to-r from-[#1a237e] to-[#283593] text-white px-4 py-2.5 rounded-xl text-sm font-medium flex items-center space-x-2 shadow-md hover:shadow-lg transition">
          <Plus size={18} />
          <span>Add Assignment</span>
        </button>
      </div>

      {/* Date Navigation */}
      <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button onClick={() => navigateDate(-1)} className="p-2 hover:bg-gray-100 rounded-lg transition">
              <ChevronLeft size={20} className="text-gray-600" />
            </button>
            <div className="flex items-center space-x-2">
              <Calendar className="text-[#1a237e]" size={20} />
              <span className="text-lg font-semibold text-gray-900">
                {new Date(selectedDate).toLocaleDateString('en-IN', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            </div>
            <button onClick={() => navigateDate(1)} className="p-2 hover:bg-gray-100 rounded-lg transition">
              <ChevronRight size={20} className="text-gray-600" />
            </button>
          </div>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1a237e]/20 focus:border-[#1a237e] outline-none"
            data-testid="roster-date-input"
          />
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-[#1a237e] to-[#283593] rounded-xl p-4 text-white">
          <div className="flex items-center space-x-2">
            <Building className="opacity-70" size={18} />
            <p className="text-sm opacity-80">Company Staff</p>
          </div>
          <p className="text-3xl font-bold mt-1">{stats.totalCompany}</p>
        </div>
        <div className="bg-gradient-to-br from-[#e65100] to-[#f57c00] rounded-xl p-4 text-white">
          <div className="flex items-center space-x-2">
            <Users className="opacity-70" size={18} />
            <p className="text-sm opacity-80">Contractor Staff</p>
          </div>
          <p className="text-3xl font-bold mt-1">{stats.totalContractor}</p>
        </div>
        <div className="bg-gradient-to-br from-amber-400 to-orange-400 rounded-xl p-4 text-white">
          <div className="flex items-center space-x-2">
            <Sun className="opacity-70" size={18} />
            <p className="text-sm opacity-80">Day Shift (6AM-6PM)</p>
          </div>
          <p className="text-3xl font-bold mt-1">{stats.dayShift}</p>
        </div>
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-4 text-white">
          <div className="flex items-center space-x-2">
            <Moon className="opacity-70" size={18} />
            <p className="text-sm opacity-80">Night Shift (6PM-6AM)</p>
          </div>
          <p className="text-3xl font-bold mt-1">{stats.nightShift}</p>
        </div>
      </div>

      {/* Employee Type Tabs */}
      <div className="flex space-x-2 bg-gray-100 p-1 rounded-xl w-fit">
        {['Company', 'Contractor'].map((type) => (
          <button
            key={type}
            onClick={() => setActiveTab(type)}
            className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-all flex items-center space-x-2 ${
              activeTab === type
                ? 'bg-white text-[#1a237e] shadow-md'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            data-testid={`tab-${type.toLowerCase()}`}
          >
            {type === 'Company' ? <Building size={16} /> : <Users size={16} />}
            <span>{type} Employees</span>
          </button>
        ))}
      </div>

      {/* Shift-wise Roster Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Day Shift Card */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Sun size={24} />
                <div>
                  <h3 className="font-bold text-lg">Day Shift</h3>
                  <p className="text-sm opacity-80">06:00 AM - 06:00 PM</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">{filteredRoster.filter(r => r.shift === 'Day').length}</p>
                <p className="text-sm opacity-80">assigned</p>
              </div>
            </div>
          </div>
          <div className="p-4">
            {filteredRoster.filter(r => r.shift === 'Day').length > 0 ? (
              <div className="space-y-3">
                {filteredRoster.filter(r => r.shift === 'Day').map((person) => (
                  <div key={person.id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 bg-gradient-to-br from-amber-400 to-orange-400 rounded-full flex items-center justify-center text-white font-bold">
                        {person.employee.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{person.employee}</p>
                        <p className="text-xs text-gray-500">{person.role} • {person.section}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(person.status)}`}>
                      {person.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">No {activeTab.toLowerCase()} employees assigned to day shift</p>
            )}
          </div>
        </div>

        {/* Night Shift Card */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Moon size={24} />
                <div>
                  <h3 className="font-bold text-lg">Night Shift</h3>
                  <p className="text-sm opacity-80">06:00 PM - 06:00 AM</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold">{filteredRoster.filter(r => r.shift === 'Night').length}</p>
                <p className="text-sm opacity-80">assigned</p>
              </div>
            </div>
          </div>
          <div className="p-4">
            {filteredRoster.filter(r => r.shift === 'Night').length > 0 ? (
              <div className="space-y-3">
                {filteredRoster.filter(r => r.shift === 'Night').map((person) => (
                  <div key={person.id} className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                    <div className="flex items-center space-x-3">
                      <div className="h-10 w-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                        {person.employee.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{person.employee}</p>
                        <p className="text-xs text-gray-500">{person.role} • {person.section}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(person.status)}`}>
                      {person.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4">No {activeTab.toLowerCase()} employees assigned to night shift</p>
            )}
          </div>
        </div>
      </div>

      {/* Full Roster Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-slate-50">
          <div className="flex items-center space-x-2">
            <Package className="text-[#1a237e]" size={20} />
            <h3 className="text-lg font-bold text-gray-900">Complete Roster - {activeTab} Employees</h3>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Employee</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Role</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Shift</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Time</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Section</th>
                <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredRoster.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-4">
                    <div className="flex items-center space-x-3">
                      <div className={`h-9 w-9 rounded-full flex items-center justify-center text-white font-bold ${
                        item.shift === 'Day' 
                          ? 'bg-gradient-to-br from-amber-400 to-orange-400' 
                          : 'bg-gradient-to-br from-indigo-500 to-purple-500'
                      }`}>
                        {item.employee.charAt(0)}
                      </div>
                      <span className="text-sm font-medium text-gray-900">{item.employee}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-600">{item.role}</td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center space-x-1 px-2.5 py-1 rounded-lg text-xs font-medium border ${getShiftColor(item.shift)}`}>
                      {item.shift === 'Day' ? <Sun size={12} /> : <Moon size={12} />}
                      <span>{item.shift}</span>
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm font-mono text-gray-600">{item.time}</td>
                  <td className="px-4 py-4 text-sm text-gray-900">{item.section}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                      {item.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default RawMaterialDutyRoster;
